import React, { useState, useMemo } from 'react';
import { useNavigation } from '../context/NavigationContext';
import { PUBLIC_TEMPLATES } from '../data/publicData';
import { PublicTemplate, SectorCategory } from '../types/publicTypes';
import { TemplateCard } from '../components/ui/TemplateCard';
import { TemplatePreviewModal } from '../components/ui/TemplatePreviewModal';
import { SectionHeading } from '../components/ui/SectionHeading';
import {
  Search,
  Filter,
  Sparkles,
  ArrowRight,
  Layers,
  CheckCircle2,
} from 'lucide-react';

export const TemplatesPage: React.FC = () => {
  const { navigate } = useNavigation();
  const [selectedCategory, setSelectedCategory] = useState<SectorCategory>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [previewTemplate, setPreviewTemplate] = useState<PublicTemplate | null>(null);

  const categories: { id: SectorCategory; label: string }[] = [
    { id: 'ALL', label: 'Tüm Temalar' },
    { id: 'CORPORATE', label: 'Kurumsal & Holding' },
    { id: 'YACHT_RENTAL', label: 'Yat Kiralama & Mavi Tur' },
    { id: 'LEGAL', label: 'Hukuk & Avukatlık' },
    { id: 'HEALTH', label: 'Sağlık & Klinik' },
    { id: 'ARCHITECTURE', label: 'Mimarlık & İnşaat' },
    { id: 'GASTRONOMY', label: 'Restoran & Gastronomi' },
  ];

  const filteredTemplates = useMemo(() => {
    const q = (searchQuery || '').toLowerCase();
    return PUBLIC_TEMPLATES.filter((tpl) => {
      const matchCategory =
        selectedCategory === 'ALL' || tpl.category === selectedCategory;
      const matchSearch =
        (tpl.name || '').toLowerCase().includes(q) ||
        (tpl.description || '').toLowerCase().includes(q) ||
        (tpl.categoryName || '').toLowerCase().includes(q);
      return matchCategory && matchSearch;
    });
  }, [selectedCategory, searchQuery]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 pt-28 sm:pt-36 pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <SectionHeading
          badge="Tema & Şablon Kataloğu"
          title="Sektörünüz İçin Özel Tasarlanmış Premium Temalar"
          subtitle="Tüm şablonlar mobil uyumlu, arama motoru optimizasyonlu ve dakikalar içinde kişiselleştirilebilir hazır bileşenlerle gelir."
        />

        {/* Filter and Search Bar */}
        <div className="mt-12 flex flex-col md:flex-row items-center justify-between gap-4 p-3 bg-slate-900/90 border border-slate-800 rounded-2xl">
          {/* Category Pills */}
          <div className="flex items-center gap-1.5 overflow-x-auto w-full md:w-auto pb-2 md:pb-0 scrollbar-none">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-3.5 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all ${
                  selectedCategory === cat.id
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Search Input */}
          <div className="relative w-full md:w-64">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Şablon veya sektör ara..."
              className="w-full pl-9 pr-4 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition"
            />
          </div>
        </div>

        {/* Templates Count indicator */}
        <div className="mt-6 flex items-center justify-between text-xs text-slate-400">
          <span>
            Toplam <strong className="text-white">{filteredTemplates.length}</strong> şablon gösteriliyor
          </span>
          {selectedCategory !== 'ALL' && (
            <button
              onClick={() => setSelectedCategory('ALL')}
              className="text-blue-400 hover:underline"
            >
              Filtreyi Temizle
            </button>
          )}
        </div>

        {/* Templates Grid */}
        {filteredTemplates.length > 0 ? (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredTemplates.map((tpl) => (
              <TemplateCard
                key={tpl.id}
                template={tpl}
                onPreview={(t) => setPreviewTemplate(t)}
              />
            ))}
          </div>
        ) : (
          <div className="mt-16 text-center py-16 p-8 rounded-3xl bg-slate-900/40 border border-slate-800">
            <Layers className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h4 className="text-lg font-bold text-white mb-1">
              Aramanıza uygun şablon bulunamadı
            </h4>
            <p className="text-xs text-slate-400 max-w-sm mx-auto mb-6">
              Farklı bir anahtar kelime deneyebilir veya tüm kategorileri listeleyebilirsiniz.
            </p>
            <button
              onClick={() => {
                setSelectedCategory('ALL');
                setSearchQuery('');
              }}
              className="px-5 py-2.5 rounded-xl bg-blue-600 text-white text-xs font-semibold hover:bg-blue-500 transition"
            >
              Tüm Şablonları Göster
            </button>
          </div>
        )}

        {/* Bottom Banner */}
        <div className="mt-20 p-8 sm:p-12 rounded-3xl bg-slate-900 border border-slate-800 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-1">
              Aradığınız sektöre özel tasarım bulamadınız mı?
            </h3>
            <p className="text-xs sm:text-sm text-slate-400">
              Tüm şablonlarımızın içeriğini ve modüllerini görsel editörümüzle dilediğiniz gibi özelleştirebilirsiniz.
            </p>
          </div>
          <button
            onClick={() => navigate('/iletisim')}
            className="px-6 py-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-xs sm:text-sm font-semibold border border-slate-700 whitespace-nowrap transition"
          >
            Özel Tasarım Danışmanlığı Alın
          </button>
        </div>
      </div>

      {/* Live Preview Modal */}
      {previewTemplate && (
        <TemplatePreviewModal
          template={previewTemplate}
          onClose={() => setPreviewTemplate(null)}
        />
      )}
    </div>
  );
};
